// src/pages/admin/AdminSubscriptionsManagement.js
// Admin : suivi & gestion des abonnements vendeurs — multivendeur
// Maxula

import React, { useState, useEffect, useCallback } from 'react';
import { FiCheck, FiX, FiPlusCircle, FiPaperclip, FiExternalLink, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { subscriptionAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700'
};
const STATUS_LABEL = {
  active: 'Actif', pending: 'En attente', expired: 'Expiré', cancelled: 'Annulé'
};

const FILTERS = ['', 'pending', 'active', 'expired', 'cancelled'];

const AdminSubscriptionsManagement = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reviewSub, setReviewSub] = useState(null);
  const [extendSub, setExtendSub] = useState(null);
  const [extendDays, setExtendDays] = useState('30');
  const [confirmBox, setConfirmBox] = useState(null);

  const load = useCallback(async (status) => {
    try {
      setLoading(true);
      const res = await subscriptionAPI.getAllAdmin(status || undefined);
      setSubs(res.data?.data || []);
    } catch (e) {
      toast.error('Erreur de chargement des abonnements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const activate = async (s) => {
    try {
      setBusyId(s._id);
      await subscriptionAPI.activate(s._id);
      toast.success('Abonnement activé');
      setReviewSub(null);
      load(filter);
    } catch (e) {
      toast.error('Erreur lors de l\'activation');
    } finally { setBusyId(null); }
  };

  const doCancel = async (s) => {
    try {
      setBusyId(s._id);
      await subscriptionAPI.cancel(s._id, "Annule par admin");
      toast.success("Abonnement annule");
      setReviewSub(null);
      load(filter);
    } catch (e) {
      toast.error("Erreur lors de l'annulation");
    } finally { setBusyId(null); }
  };
  const cancel = (s) => setConfirmBox({
    title: "Annuler l'abonnement",
    message: "Confirmer l'annulation de cet abonnement ?",
    confirmLabel: "Annuler l'abonnement",
    onConfirm: () => doCancel(s)
  });

  const doExtend = async () => {
    const s = extendSub;
    const days = parseInt(extendDays, 10);
    if (!s || !Number.isFinite(days) || days === 0) { toast.error("Nombre de jours invalide"); return; }
    try {
      setBusyId(s._id);
      await subscriptionAPI.extend(s._id, days);
      toast.success(`Prolonge de ${days} jours`);
      setExtendSub(null);
      load(filter);
    } catch (e) {
      toast.error("Erreur lors de la prolongation");
    } finally { setBusyId(null); }
  };
  const extend = (s) => { setExtendDays("30"); setExtendSub(s); };

  const doRemove = async (s) => {
    try {
      setBusyId(s._id);
      await subscriptionAPI.remove(s._id);
      toast.success("Demande supprimee");
      setReviewSub(null);
      load(filter);
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally { setBusyId(null); }
  };
  const remove = (s) => setConfirmBox({
    title: "Supprimer la demande",
    message: "Supprimer definitivement cette demande ? Cette action est irreversible.",
    confirmLabel: "Supprimer",
    onConfirm: () => doRemove(s)
  });

  const doCleanup = async () => {
    try {
      setBusyId("cleanup");
      const res = await subscriptionAPI.cleanupPending();
      toast.success(res.data?.message || "Nettoyage effectue");
      load(filter);
    } catch (e) {
      toast.error("Erreur lors du nettoyage");
    } finally { setBusyId(null); }
  };
  const cleanupPending = () => setConfirmBox({
    title: "Nettoyer les demandes sans recu",
    message: "Supprimer toutes les demandes en attente qui n'ont aucun recu ? Les demandes avec recu et les abonnements actifs ne seront pas touches.",
    confirmLabel: "Nettoyer",
    onConfirm: doCleanup
  });

  const runConfirm = async () => {
    const fn = confirmBox && confirmBox.onConfirm;
    setConfirmBox(null);
    if (fn) await fn();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Abonnements</h1>
        <p className="text-gray-500">Validez les paiements (virement) et gérez les abonnements vendeurs.</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f || 'all'}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                filter === f ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {f ? STATUS_LABEL[f] : 'Tous'}
            </button>
          ))}
        </div>
        <button
          onClick={cleanupPending}
          disabled={busyId === 'cleanup'}
          title="Supprimer les demandes en attente sans reçu"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
        >
          <FiTrash2 size={14} /> Nettoyer les demandes sans reçu
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left">Vendeur</th>
                <th className="px-4 py-3 text-left">Pack</th>
                <th className="px-4 py-3 text-left">Cycle</th>
                <th className="px-4 py-3 text-right">Montant</th>
                <th className="px-4 py-3 text-left">Paiement</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-left">Expire</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subs.map((s) => (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{(s.merchant || s.merchantId)?.businessName || '—'}</div>
                    <div className="text-xs text-gray-400">{(s.merchant || s.merchantId)?.email}</div>
                  </td>
                  <td className="px-4 py-3">{s.planSnapshot?.name || s.planId?.name || '—'}</td>
                  <td className="px-4 py-3">{s.billingCycle === 'yearly' ? 'Annuel' : 'Mensuel'}</td>
                  <td className="px-4 py-3 text-right">{s.amount ? `${s.amount} MAD` : 'Gratuit'}</td>
                  <td className="px-4 py-3">
                    <div className="capitalize">{s.paymentMethod}</div>
                    <div className="text-xs text-gray-400">{s.paymentStatus}</div>
                    {s.paymentRef && <div className="text-xs text-gray-400 font-mono">{s.paymentRef}</div>}
                    {s.proofUrl ? (
                      <button type="button" onClick={() => setReviewSub(s)}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline">
                        <FiPaperclip size={12} /> Voir le reçu
                      </button>
                    ) : (
                      s.paymentMethod !== 'free' && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs text-gray-300">
                          <FiPaperclip size={12} /> Aucun reçu
                        </div>
                      )
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[s.status] || ''}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {s.endDate ? new Date(s.endDate).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {s.status !== 'active' && (
                      <button onClick={() => activate(s)} disabled={busyId === s._id}
                        title="Activer" className="p-2 text-green-600 hover:bg-green-50 rounded">
                        <FiCheck />
                      </button>
                    )}
                    {s.status === 'active' && (
                      <button onClick={() => extend(s)} disabled={busyId === s._id}
                        title="Prolonger" className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <FiPlusCircle />
                      </button>
                    )}
                    {s.status !== 'cancelled' && (
                      <button onClick={() => cancel(s)} disabled={busyId === s._id}
                        title="Annuler" className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <FiX />
                      </button>
                    )}
                    {s.status !== 'active' && (
                      <button onClick={() => remove(s)} disabled={busyId === s._id}
                        title="Supprimer définitivement" className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <FiTrash2 />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    Aucun abonnement.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {reviewSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setReviewSub(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Vérification du paiement</h3>
                <p className="text-xs text-gray-500">Contrôlez le reçu avant de valider ou refuser.</p>
              </div>
              <button onClick={() => setReviewSub(null)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                <FiX size={20} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2 text-sm">
                {[
                  ['Vendeur', (reviewSub.merchant || reviewSub.merchantId)?.businessName || '—'],
                  ['Email', (reviewSub.merchant || reviewSub.merchantId)?.email || '—'],
                  ['Pack', reviewSub.planSnapshot?.name || reviewSub.planId?.name || '—'],
                  ['Cycle', reviewSub.billingCycle === 'yearly' ? 'Annuel' : 'Mensuel'],
                  ['Montant', reviewSub.amount ? `${reviewSub.amount} MAD` : 'Gratuit'],
                  ['Méthode', reviewSub.paymentMethod || '—'],
                  ['Référence', reviewSub.paymentRef || '—'],
                  ['Reçu envoyé le', reviewSub.proofUploadedAt ? new Date(reviewSub.proofUploadedAt).toLocaleString('fr-FR') : '—']
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-gray-50 py-1.5">
                    <span className="text-gray-500">{k}</span>
                    <span className="font-medium text-gray-900 text-right break-all">{v}</span>
                  </div>
                ))}
                <div className="flex justify-between gap-3 py-1.5">
                  <span className="text-gray-500">Statut</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[reviewSub.status] || ''}`}>
                    {STATUS_LABEL[reviewSub.status] || reviewSub.status}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-500 mb-2">Reçu de paiement</div>
                {reviewSub.proofUrl ? (
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                    <img
                      src={normalizeImagePath(reviewSub.proofUrl)}
                      alt="Reçu de paiement"
                      className="w-full max-h-[360px] object-contain bg-white"
                      onError={(e) => { e.currentTarget.style.display = 'none'; const n = e.currentTarget.nextSibling; if (n) n.style.display = 'block'; }}
                    />
                    <div className="hidden p-6 text-center text-sm text-gray-500">
                      Aperçu indisponible (fichier non image).
                    </div>
                    <a href={normalizeImagePath(reviewSub.proofUrl)} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 border-t border-gray-200">
                      <FiExternalLink size={14} /> Ouvrir en plein écran
                    </a>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-400">
                    Aucun reçu fourni.
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setReviewSub(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Fermer
              </button>
              {reviewSub.status !== 'cancelled' && (
                <button onClick={() => doCancel(reviewSub)} disabled={busyId === reviewSub._id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg disabled:opacity-50">
                  <FiX size={16} /> Refuser le paiement
                </button>
              )}
              {reviewSub.status !== 'active' && (
                <button onClick={() => activate(reviewSub)} disabled={busyId === reviewSub._id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  <FiCheck size={16} /> Valider le paiement
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {extendSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setExtendSub(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Prolonger l&apos;abonnement</h3>
              <p className="text-xs text-gray-500">{(extendSub.merchant || extendSub.merchantId)?.businessName} — {extendSub.planSnapshot?.name || ''}</p>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de jours</label>
              <input type="number" min="1" value={extendDays} autoFocus
                onChange={(e) => setExtendDays(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doExtend(); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setExtendSub(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={doExtend} disabled={busyId === extendSub._id}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
                <FiPlusCircle size={16} /> Prolonger
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmBox(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <h3 className="text-lg font-bold text-gray-900">{confirmBox.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{confirmBox.message}</p>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setConfirmBox(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={runConfirm}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">
                {confirmBox.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionsManagement;
